import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { RxCheck } from "react-icons/rx";
import { ClipLoader } from "react-spinners";

import { CardTypeEnum, VariantFinishEnum } from "@sorcery/db";

import { ModalTypeEnum, useModal } from "~/providers/ModalProvider";
import { useUser } from "~/providers/UserProvider";
import { fullCollectionlistJotai } from "~/state/collectionJotai";
import { avatarJotai, deckJotai, decklistJotai, trackingJotai } from "~/state/deckJotai";
import type { CardType, VariantType } from "~/types/card";
import type { DeckCardType } from "~/types/deck";
import { CardThreshholds } from "../../../molecules/card/CardThreshholds";

export const DecklistItem = ({
  deckCard,
  onCardHover,
}: {
  deckCard: DeckCardType;
  onCardHover?: (squiggle: { card: CardType; variant: VariantType }) => void;
}) => {
  const { user } = useUser();
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const deck = useAtomValue(deckJotai)!;
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const avatar = useAtomValue(avatarJotai)!;
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const decklist = useAtomValue(decklistJotai)!;
  const tracking = useAtomValue(trackingJotai);
  const fullCollectionlist = useAtomValue(fullCollectionlistJotai);

  const { openModal } = useModal();
  const isAuthor = useMemo(
    () => !!user && !!deck.user && user.id === deck.user.id,
    [deck.user, user],
  );

  const itemsInCollection = useMemo(
    () =>
      fullCollectionlist
        ?.find((c) => c.card.id === deckCard.cardId)
        ?.groupings.reduce((acc, grouping) => acc + grouping.items.length, 0) ?? 0,
    [fullCollectionlist, deckCard.cardId],
  );

  const itemsInDeck = useMemo(
    () =>
      (avatar.cardId === deckCard.cardId ? avatar.quantity : 0) +
      (decklist.find((c) => c.cardId === deckCard.cardId)?.quantity ?? 0),
    [avatar, decklist, deckCard.cardId],
  );

  const enoughInCollection = useMemo(
    () => tracking && itemsInCollection >= itemsInDeck,
    [tracking, itemsInCollection, itemsInDeck],
  );

  const collectionShading = useMemo(() => {
    return tracking && fullCollectionlist
      ? !enoughInCollection || itemsInCollection === 0
        ? "text-red"
        : "text-[#1fe9ff]"
      : "";
  }, [fullCollectionlist, enoughInCollection, itemsInCollection, tracking]);

  return (
    <div
      className={`hover:text-primary flex cursor-pointer flex-row justify-between p-1 ${tracking && collectionShading}`}
      onMouseEnter={() =>
        onCardHover?.({
          card: deckCard.card,
          variant: deckCard.variant,
        })
      }
      onClick={() => {
        isAuthor
          ? openModal(ModalTypeEnum.DECKGALLERY, {
              card: deckCard.card,
            })
          : openModal(ModalTypeEnum.CARDGALLERY, {
              card: deckCard.card,
            });
      }}
      key={deckCard.id}
    >
      <div>
        {tracking && (
          <span className={"inline-block w-[20px] pl-2 pr-1 duration-500 animate-in fade-in"}>
            {!fullCollectionlist ? (
              <span className="pr-0.5">
                <ClipLoader
                  className={"!border-l-white !border-r-white !border-t-white"}
                  size={12}
                />
              </span>
            ) : enoughInCollection ? (
              <RxCheck className={"inline"} size={14} />
            ) : (
              <span className={"ml-1 whitespace-nowrap"}>{itemsInCollection} /</span>
            )}
          </span>
        )}

        <span className="inline-block w-[30px] px-2">{deckCard.quantity}</span>
        <span
          className={`transition-all ${
            deckCard.variant.finish === VariantFinishEnum.Foil && "underline-sparkle relative"
          }`}
        >
          {deckCard.card.name}
        </span>
      </div>

      {!tracking && (
        <div className="flex h-full flex-row items-center duration-500 animate-in fade-in">
          {/* biome-ignore lint/style/noNonNullAssertion: <explanation> */}
          <CardThreshholds guardian={deckCard.card.guardian!} />

          {/* Mana cost */}
          {(!!deckCard.card.guardian?.cost ||
            deckCard.card.guardian?.type === CardTypeEnum.Magic) && (
            <span
              className={
                "ml-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-[#dedcdc] text-center text-black"
              }
              style={{
                boxShadow: "-0 0.12em 0.2em #222, 0.1em 0.08em 0.2em #222",
              }}
            >
              {deckCard.card.guardian?.cost ?? "x"}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
